import EventIcon from '@mui/icons-material/Event';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import { ButtonBase } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { firmSummaryUrl, signInPath } from 'common/config/urls';
import { PORTFOLIO_VALUATION } from 'common/constants/auth';
import { useStore } from 'common/store';
import type { UseStoreValues } from 'common/types/store';
import { Tooltip } from 'components/ui';
import { getThemeSpacingCssPx } from 'components/ui/utilities/theme';
import { getCssPx } from 'components/ui/utilities/zoom';
import { WORKBOARD_CURRENT_VALUATIONS_URL } from 'dashboard409a/common/config/urls';
import { getPreviousWorkboardRoute } from 'dashboard409a/utilities/navigation';
import {
  clearAppAutoSelect,
  getSafePreviousPortfolioRoute,
  saveAppAutoSelect,
  savePreviousPortfolioRoute,
} from 'pages/Auth/utils';
import { FIRM_PRIMARY_NAVIGATION_TEST_IDS } from '../PrimaryNavigation.testIds';
import type { AppSwitcherButtonStyleProps, AppSwitcherProps, ZoomStyleProps } from './AppSwitcher.types';

const shouldForwardZoomProp = (prop: PropertyKey) => prop !== 'zoomLevel';
const shouldForwardButtonProp = (prop: PropertyKey) => prop !== 'zoomLevel' && prop !== 'isActive';

const AppSwitcherRoot = styled('div', {
  shouldForwardProp: shouldForwardZoomProp,
})<ZoomStyleProps>(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: getThemeSpacingCssPx(theme, 'xs'),
  borderRadius: getCssPx(theme.shape.radius.xs),
  backgroundColor: theme.palette.navigation.primary.chip.background,
}));

const AppSwitcherButton = styled(ButtonBase, {
  shouldForwardProp: shouldForwardButtonProp,
})<AppSwitcherButtonStyleProps>(({ isActive, theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: getCssPx(24),
  height: getCssPx(24),
  padding: getThemeSpacingCssPx(theme, 'xs'),
  borderRadius: getCssPx(2),
  boxSizing: 'border-box',
  color: theme.palette.navigation.primary.iconButton.color,
  backgroundColor: isActive ? theme.palette.primary.main : 'transparent',
  transition: theme.transitions.create('background-color', {
    duration: 160,
    easing: theme.transitions.easing.easeInOut,
  }),
  '& svg': {
    fontSize: getCssPx(16),
  },
  '&:hover': {
    backgroundColor: isActive
      ? theme.palette.primary.main
      : alpha(theme.palette.navigation.primary.iconButton.hoverBackgroundBase, 0.1),
  },
}));

const AppSwitcher = ({ zoomLevel, activeApp = 'valuations' }: AppSwitcherProps) => {
  const navigate = useNavigate();
  const [{ user }] = useStore() as UseStoreValues;

  const handleWorkboardClick = () => {
    savePreviousPortfolioRoute(globalThis.window.location.pathname, user?.id);
    clearAppAutoSelect();
    navigate(getPreviousWorkboardRoute(user?.id) || WORKBOARD_CURRENT_VALUATIONS_URL);
  };

  const handlePortfolioClick = () => {
    const savedPortfolioRoute = getSafePreviousPortfolioRoute(user?.id);
    const singleFirmSlug = user?.firms_permissions?.length === 1 ? user.firms_permissions[0]?.slug : null;

    if (savedPortfolioRoute) {
      clearAppAutoSelect();
      navigate(savedPortfolioRoute);
      return;
    }

    if (singleFirmSlug) {
      clearAppAutoSelect();
      navigate(firmSummaryUrl(singleFirmSlug));
      return;
    }

    saveAppAutoSelect(PORTFOLIO_VALUATION);
    navigate(signInPath);
  };

  const isValuationsActive = activeApp === 'valuations';

  return (
    <AppSwitcherRoot zoomLevel={zoomLevel} data-testid={FIRM_PRIMARY_NAVIGATION_TEST_IDS.appSwitcher}>
      <Tooltip title="Valuations" placement="bottom">
        <AppSwitcherButton
          isActive={isValuationsActive}
          zoomLevel={zoomLevel}
          aria-label={isValuationsActive ? 'Valuations' : 'Switch to Valuations'}
          {...(isValuationsActive ? { 'aria-pressed': 'true' } : { onClick: handlePortfolioClick })}
          data-testid={FIRM_PRIMARY_NAVIGATION_TEST_IDS.appSwitcherScalar}>
          <ListAltOutlinedIcon />
        </AppSwitcherButton>
      </Tooltip>
      <Tooltip title="Workboard" placement="bottom">
        <AppSwitcherButton
          isActive={!isValuationsActive}
          zoomLevel={zoomLevel}
          aria-label={isValuationsActive ? 'Switch to Workboard' : 'Workboard'}
          {...(!isValuationsActive ? { 'aria-pressed': 'true' } : { onClick: handleWorkboardClick })}
          data-testid={FIRM_PRIMARY_NAVIGATION_TEST_IDS.appSwitcherWorkboard}>
          <EventIcon />
        </AppSwitcherButton>
      </Tooltip>
    </AppSwitcherRoot>
  );
};

export default AppSwitcher;
